// per docs/caiet-uc-spec.md §UC22-02 §UC22-03 §UC22-04
// per docs/shape-ops-correction-admin-register.md §2 D6 (T091; CF-41): the decision may be a month.
// The decision page, per wireframes/react/src/pages/AdminSolicitari.tsx. The
// decision used to be taken in a modal over six raw identifiers: a medicine
// code, a lot, an IDNO, an IDNP and two dates. Everything the administrator
// needs to judge the request is on this page, including what the approval
// writes, and a decided request stays readable here with its decider and its
// comment instead of vanishing from the product.
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudBanner,
  MudButton,
  MudCheckbox,
  MudDateInput,
  MudIcon,
  MudModal,
  MudInfoBox,
  MudTable,
  MudTag,
  MudTextarea,
} from '@egov-moldova/mud-react';
import {
  Breadcrumb,
  dateInputToIso,
  EmptyRegisterMessage,
  EmptyState,
  ExpiryMonthInput,
  formatDate,
  formatDateTime,
  formatExpiry,
  isEndOfMonth,
  isoToDateInput,
  onDateCommit,
  onModalDismiss,
  PageHeader,
  RouterLink,
  useToast,
} from '@simsm/ui-shared';
import { QueryState } from '../../components/QueryState/QueryState';
import { apiErrorKey } from '../../app/api-client';
import { approveCorrection, rejectCorrection } from '../../features/corrections/api';
import { ADMIN_CORRECTION_COUNTS_KEY } from '../../features/corrections/use-pending-corrections-count';
import {
  approvalChangesNothing,
  getAdminCorrection,
  statusLabelKey,
  statusSemantic,
} from '../../features/corrections/admin-api';
import '../operator/reporting/expiry-month.css';
import './corrections.css';

// An expiry as it was given (approval shape D4): a month as a month, a day as a
// day. formatExpiry alone reads any month-end day as a month.
function statedExpiry(expiry: string, precision: string): string {
  return precision === 'MONTH' ? formatExpiry(expiry.slice(0, 7)) : formatDate(expiry);
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="simsm-corrections__definition-row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export function CorrectionRequestDetailPage(): JSX.Element {
  const { t } = useTranslate();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const toast = useToast();

  // The screen's only editable control (UC22-02) holds what the reader typed —
  // the masked display string mud-date-input echoes — and derives the ISO from
  // it. It used to hold the ISO and render `isoToDateInput(iso)` back into the
  // input: every incomplete keystroke pushed `isoValue: null` into state, and
  // `@lit/react`'s createComponent assigns `node.value` on each render with no
  // dirty check, so select-all + `15092028` produced `50/92/028` and one
  // Backspace cleared the field (audit A1). Nothing about the mask was wrong;
  // the binding was fighting it.
  const [decidedExpiryInput, setDecidedExpiryInput] = useState('');
  // The wireframe's "Fără zi" (without day): the decision is a month, kept as
  // one (CF-41). The month field holds its month's last day, the date every
  // comparison below reads; the approval sends YYYY-MM so core keeps the
  // precision (docs/shape-ops-correction-admin-register.md D6).
  const [withoutDay, setWithoutDay] = useState(false);
  const [decidedMonth, setDecidedMonth] = useState('');
  const decidedExpiry = withoutDay ? decidedMonth : dateInputToIso(decidedExpiryInput);
  const decidedExpiryWire = withoutDay ? decidedMonth.slice(0, 7) : decidedExpiry;
  const [decisionComment, setDecisionComment] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [attempted, setAttempted] = useState<'approve' | 'reject' | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const detail = useQuery({
    queryKey: ['admin-correction', id],
    queryFn: () => getAdminCorrection(id as string),
    enabled: Boolean(id),
  });

  const request = detail.data?.request;
  const impact = detail.data?.impact;
  const medicine = detail.data?.medicine;
  const registrar = detail.data?.registrar;
  const requestSite = detail.data?.requestSite ?? null;
  const sites = detail.data?.sites ?? [];
  const lotHistory = detail.data?.lotHistory ?? [];

  // caiet UC22-04 / TC-UC22-04-03: the lot's expiry changes, with who and when
  // (docs/shape-ops-expiry-correction-approval.md D5).
  const historyColumns = useMemo(
    () => [
      { key: 'changedAt', label: t('approvals.lot_history.column.changed_at') },
      { key: 'previousExpiry', label: t('approvals.lot_history.column.previous_expiry') },
      { key: 'newExpiry', label: t('approvals.lot_history.column.new_expiry') },
      { key: 'changedBy', label: t('approvals.lot_history.column.changed_by') },
      { key: 'request', label: t('approvals.lot_history.column.request') },
    ],
    [t],
  );
  const historyRows = lotHistory.map((entry) => ({
    id: entry.correctionRequestId,
    changedAt: formatDateTime(entry.changedAtIso),
    previousExpiry: statedExpiry(entry.previousExpiry, entry.previousPrecision),
    newExpiry: statedExpiry(entry.newExpiry, entry.newPrecision),
  }));

  const isPending = request?.status === 'PENDING';
  // While the request is open, what matters is the lot's date right now, which
  // can have drifted from the copy taken when the request was raised. Once it is decided, the live value is the decision's own result, so
  // the record shows the date as reported at the time.
  const reportedExpiry =
    (isPending ? impact?.reportedExpiry : request?.currentExpiry) || request?.currentExpiry || '';
  // The approval rewrites every row of the lot (UC22-04), and the rows can carry
  // more than one date: an Operator asks at its own locations (CF-43) while
  // another location reported the lot later. The open request then names each.
  const carriedExpiries = impact?.carriedExpiries ?? [];
  const reportedLabel =
    isPending && carriedExpiries.length > 1
      ? carriedExpiries.map((expiry) => formatDate(expiry)).join(', ')
      : formatDate(reportedExpiry);

  // A proposal on a month's last day reads as a month on every screen
  // (formatExpiry), so the decision starts as one too.
  useEffect(() => {
    if (!request) return;
    const month = isEndOfMonth(request.proposedExpiry);
    setWithoutDay(month);
    setDecidedMonth(month ? request.proposedExpiry : '');
    setDecidedExpiryInput(month ? '' : isoToDateInput(request.proposedExpiry));
  }, [request]);

  // The wireframe refuses an approved date equal to the reported one. UC22-02
  // does not require it; we adopt it deliberately, because a no-op correction
  // would still write the lot's history and an MLog event (UC22-04).
  // core-service enforces the same rule, this only says so earlier.
  const wellFormed = /^\d{4}-\d{2}-\d{2}$/.test(decidedExpiry);
  const isNoOp = wellFormed && approvalChangesNothing(decidedExpiry, carriedExpiries);
  const decidedExpiryValid = wellFormed && !isNoOp;
  const showDateError = attempted === 'approve' && !decidedExpiryValid;

  const rejectReasonMissing = attempted === 'reject' && !rejectionReason.trim();
  const backTo = `/system-admin/approvals${location.search}`;

  // The decision closes this screen and returns to a queue the request has just
  // left, so the only trace of what was decided would be its absence. The toast
  // outlives the navigation because the host is mounted above the router.
  function afterDecision(outcome: 'approved' | 'rejected'): void {
    void queryClient.invalidateQueries({ queryKey: ['admin-corrections'] });
    void queryClient.invalidateQueries({ queryKey: ADMIN_CORRECTION_COUNTS_KEY });
    void queryClient.invalidateQueries({ queryKey: ['admin-correction', id] });
    toast({
      variant: outcome === 'approved' ? 'success' : 'info',
      title: outcome === 'approved' ? t('approvals.toast.approved') : t('approvals.toast.rejected'),
      text: medicine?.name || undefined,
    });
    navigate(backTo);
  }

  const approve = useMutation({
    mutationFn: () =>
      approveCorrection(id as string, { decidedExpiry: decidedExpiryWire, decisionComment }),
    onSuccess: () => afterDecision('approved'),
    onError: (error: unknown) => setErrorKey(apiErrorKey(error, 'approvals.error.approve_failed')),
  });

  const reject = useMutation({
    mutationFn: () => rejectCorrection(id as string, rejectionReason),
    onSuccess: () => {
      setRejectOpen(false);
      afterDecision('rejected');
    },
    onError: (error: unknown) => {
      setRejectOpen(false);
      setErrorKey(apiErrorKey(error, 'approvals.error.reject_failed'));
    },
  });

  function tryApprove(): void {
    if (!decidedExpiryValid) {
      setAttempted('approve');
      return;
    }
    setAttempted(null);
    setErrorKey(null);
    approve.mutate();
  }

  function tryReject(): void {
    if (!rejectionReason.trim()) {
      setAttempted('reject');
      return;
    }
    setAttempted(null);
    setErrorKey(null);
    reject.mutate();
  }

  const dateHelper = showDateError
    ? isNoOp
      ? t('approvals.decision.date_same_as_reported')
      : t('approvals.decision.date_invalid')
    : wellFormed && request && decidedExpiry === request.proposedExpiry
      ? t('approvals.decision.date_is_proposed')
      : wellFormed && request
        ? t('approvals.decision.date_differs', { date: formatExpiry(request.proposedExpiry) })
        : t('approvals.decision.date_format');

  return (
    <section className="simsm-page simsm-corrections">
      <QueryState
        query={detail}
        isEmpty={!request}
        empty={
          /* The wireframe draws this exact page (AdminSolicitari.tsx:88): the
             `.empty` card, the object's own glyph, "Solicitarea nu există", and
             one outlined button back to the register it came from. Unframed,
             like all four not-found empties in the wireframe and unlike every
             register empty there — on a detail route the page IS the absence,
             so there is no card for a border to enclose.
             The wireframe's body names the identifier ("Identificatorul {id} nu
             e în lista solicitărilor") because its route carries a human one.
             Ours carries a database UUID, and cffc567 is what printing one of
             those in front of a reader cost this repository already; the body
             names the cause instead, which is the line AdminAlerte.tsx:2721
             leaves out altogether. `backTo` keeps the queue's own criteria, so
             the button returns the reader to the list they left, not to its
             first page. */
          <EmptyState
            framed={false}
            icon="document"
            title={t('approvals.detail.not_found.title')}
            body={t('approvals.detail.not_found.body')}
            action={
              <MudButton
                shape="circular"
                variant="neutral"
                size="md"
                onClick={() => navigate(backTo)}
              >
                {t('approvals.detail.not_found.action')}
              </MudButton>
            }
          />
        }
      >
        {request ? (
          <>
            {/* The crumb carries the list criteria back to the register. */}
            <Breadcrumb
              label={t('approvals.detail.breadcrumb')}
              crumbs={[{ label: t('approvals.title'), to: backTo }, { label: request.lot }]}
            />

            <PageHeader
              title={t('approvals.detail.title')}
              lede={t('approvals.detail.lede')}
              actions={
                <MudTag
                  variant="status"
                  semantic={statusSemantic(request.status)}
                  label={t(statusLabelKey(request.status))}
                />
              }
            />

            {!isPending ? (
              <MudBanner
                variant={request.status === 'APPROVED' ? 'info' : 'warning'}
                emphasis="subtle"
              >
                {t(
                  request.status === 'APPROVED'
                    ? 'approvals.decided.approved'
                    : 'approvals.decided.rejected',
                  {
                    when: request.decidedAtIso ? formatDateTime(request.decidedAtIso) : '',
                    who: request.decidedByIdnp || t('approvals.decided.unknown_actor'),
                  },
                )}
              </MudBanner>
            ) : null}

            <div className="simsm-corrections__sections">
              <section aria-labelledby="correction-medicine">
                <h2 id="correction-medicine">{t('approvals.section.medicine')}</h2>
                <dl className="simsm-corrections__definitions">
                  <DetailRow label={t('approvals.field.medicine_name')}>
                    {medicine?.name || t('approvals.field.unresolved')}
                  </DetailRow>
                  <DetailRow label={t('approvals.field.form')}>
                    {medicine?.pharmaceuticalForm || '–'}
                  </DetailRow>
                  <DetailRow label={t('approvals.field.dose')}>{medicine?.dose || '–'}</DetailRow>
                  <DetailRow label={t('approvals.field.dci')}>{medicine?.dci || '–'}</DetailRow>
                  <DetailRow label={t('approvals.field.manufacturer')}>
                    {medicine?.manufacturer || '–'}
                  </DetailRow>
                  <DetailRow label={t('approvals.field.code')}>
                    <span className="simsm-corrections__mono">{request.medicineCode}</span>
                  </DetailRow>
                  <DetailRow label={t('approvals.field.lot')}>
                    <span className="simsm-corrections__mono">{request.lot}</span>
                  </DetailRow>
                </dl>
              </section>

              <section aria-labelledby="correction-request">
                <h2 id="correction-request">{t('approvals.section.request')}</h2>
                <dl className="simsm-corrections__definitions">
                  <DetailRow label={t('approvals.field.registrar')}>
                    {registrar?.name || t('approvals.field.unresolved')}
                    <span className="simsm-corrections__meta simsm-corrections__mono">
                      {request.registrarId}
                    </span>
                  </DetailRow>
                  <DetailRow label={t('approvals.field.request_site')}>
                    {requestSite?.name || t('approvals.field.no_request_site')}
                  </DetailRow>
                  <DetailRow label={t('approvals.field.requested_by')}>
                    <span className="simsm-corrections__mono">{request.operatorIdnp}</span>
                  </DetailRow>
                  <DetailRow label={t('approvals.field.created_at')}>
                    {formatDateTime(request.createdAtIso)}
                  </DetailRow>
                  {/* The reason is optional (owner ruling 2026-09-15: UC51 asks for none), but
                      when the requester gave one the approver reads it (TC-UC22-02-01). */}
                  {request.requestComment?.trim() ? (
                    <DetailRow label={t('approvals.field.request_comment')}>
                      {request.requestComment}
                    </DetailRow>
                  ) : null}
                </dl>
              </section>

              <section aria-labelledby="correction-impact">
                <h2 id="correction-impact">{t('approvals.section.impact')}</h2>
                <p className="simsm-corrections__section-lede">{t('approvals.impact.lede')}</p>
                <dl className="simsm-corrections__definitions">
                  <DetailRow label={t('approvals.field.movement_rows')}>
                    {(impact?.movementRows ?? 0) === 1
                      ? t('approvals.impact.movements_one')
                      : t('approvals.impact.movements_other', {
                          count: impact?.movementRows ?? 0,
                        })}
                  </DetailRow>
                  <DetailRow label={t('approvals.field.site_count')}>
                    {(impact?.siteCount ?? 0) === 0
                      ? t('approvals.impact.sites_zero')
                      : (impact?.siteCount ?? 0) === 1
                        ? t('approvals.impact.sites_one')
                        : t('approvals.impact.sites_other', { count: impact?.siteCount ?? 0 })}
                  </DetailRow>
                  <DetailRow label={t('approvals.field.sites')}>
                    {sites.length > 0
                      ? sites.map((site) => site.name).join(', ')
                      : t('approvals.field.no_sites')}
                  </DetailRow>
                  <DetailRow label={t('approvals.field.net_quantity')}>
                    {impact?.netQuantity ?? '0'}
                  </DetailRow>
                  <DetailRow label={t('approvals.field.movement_span')}>
                    {impact?.firstMovementDate
                      ? `${formatDate(impact.firstMovementDate)} – ${formatDate(impact.lastMovementDate)}`
                      : '–'}
                  </DetailRow>
                </dl>
              </section>

              <section aria-labelledby="correction-decision">
                <h2 id="correction-decision">{t('approvals.section.decision')}</h2>
                <div className="simsm-corrections__change simsm-corrections__change--large">
                  <span className="simsm-corrections__stack">
                    <span className="simsm-corrections__meta">
                      {t('approvals.field.reported_expiry')}
                    </span>
                    <strong>{reportedLabel}</strong>
                  </span>
                  <MudIcon name="arrow-right" size={16} aria-hidden="true" />
                  <span className="simsm-corrections__stack">
                    <span className="simsm-corrections__meta">
                      {t('approvals.field.proposed_expiry')}
                    </span>
                    <strong>{formatExpiry(request.proposedExpiry)}</strong>
                  </span>
                  {!isPending && request.decidedExpiry ? (
                    <>
                      <MudIcon name="arrow-right" size={16} aria-hidden="true" />
                      <span className="simsm-corrections__stack">
                        <span className="simsm-corrections__meta">
                          {t('approvals.field.decided_expiry')}
                        </span>
                        <strong>{formatExpiry(request.decidedExpiry)}</strong>
                      </span>
                    </>
                  ) : null}
                </div>

                {isPending ? (
                  <div className="simsm-corrections__decision-form">
                    <div className="simsm-corrections__decided-expiry">
                      {withoutDay ? (
                        // Mounted afresh on each toggle, so the field starts
                        // empty (the wireframe clears it) rather than keeping
                        // what was typed in the other one.
                        <ExpiryMonthInput
                          value={decidedMonth}
                          onChange={(wireDate) => {
                            setDecidedMonth(wireDate);
                            setAttempted(null);
                          }}
                          label={t('approvals.field.decided_expiry')}
                          ariaLabel={t('approvals.decision.month_aria_label')}
                          placeholder={t('expiry.month_placeholder')}
                          required
                          invalid={showDateError}
                          {...(showDateError
                            ? { errorText: dateHelper }
                            : { helperText: dateHelper })}
                        />
                      ) : (
                        <MudDateInput
                          placeholder={t('common.date.placeholder')}
                          size="md"
                          label={t('approvals.field.decided_expiry')}
                          required={true}
                          value={decidedExpiryInput}
                          invalid={showDateError}
                          {...(showDateError
                            ? { errorText: dateHelper }
                            : { helperText: dateHelper })}
                          onMudInput={(
                            e: CustomEvent<{ value: string; isoValue: string | null }>,
                          ) => {
                            setDecidedExpiryInput(e.detail.value);
                            setAttempted(null);
                          }}
                          // AGE-BUG(date-picker-mudChange-escapes-shadow-root) ·
                          // vendor/age-design/SOURCE_COMMIT 7322a157 — picking a day
                          // in the calendar emits `mudChange` ONLY (never
                          // `mudInput`), so with `onMudInput` alone this field
                          // ignored every date chosen with the mouse: the component
                          // set its own value, the next render assigned the stale
                          // one back over it, and the reader watched the date they
                          // had just picked disappear. The guard is what makes
                          // adding the handler safe — the picker's own event escapes
                          // the shadow root into this same handler carrying
                          // `{ value: '2028-09-15' }`, the ISO day and not the
                          // masked one this state holds, which `displayToIso` cannot
                          // read. `d.value` here, not `d.isoValue`: the state is the
                          // display string the mask echoes (see the comment on it).
                          // `attempted` is deliberately NOT cleared — a commit that
                          // is still not a usable date must keep its error visible,
                          // and a commit that is one clears the error by itself
                          // through `decidedExpiryValid`.
                          onMudChange={onDateCommit((d) => setDecidedExpiryInput(d.value))}
                        />
                      )}
                      <MudCheckbox
                        size="md"
                        checked={withoutDay}
                        onMudChange={(e: CustomEvent<{ checked: boolean }>) => {
                          setWithoutDay(e.detail.checked);
                          setDecidedMonth('');
                          setDecidedExpiryInput('');
                          setAttempted(null);
                        }}
                      >
                        <span slot="label">{t('approvals.decision.without_day')}</span>
                      </MudCheckbox>
                    </div>
                    <MudTextarea
                      label={t('approvals.field.decision_comment')}
                      helperText={t('approvals.field.decision_comment_help')}
                      value={decisionComment}
                      maxLength={500}
                      onMudInput={(e: CustomEvent<{ value: string }>) =>
                        setDecisionComment(e.detail.value)
                      }
                    />
                  </div>
                ) : (
                  <dl className="simsm-corrections__definitions">
                    <DetailRow label={t('approvals.field.decided_by')}>
                      <span className="simsm-corrections__mono">
                        {request.decidedByIdnp || t('approvals.decided.unknown_actor')}
                      </span>
                    </DetailRow>
                    <DetailRow label={t('approvals.field.decided_at')}>
                      {request.decidedAtIso ? formatDateTime(request.decidedAtIso) : '–'}
                    </DetailRow>
                    <DetailRow label={t('approvals.field.decision_comment')}>
                      {request.decisionComment || t('approvals.decided.no_comment')}
                    </DetailRow>
                  </dl>
                )}
              </section>

              <section
                className="simsm-corrections__section--wide"
                aria-labelledby="correction-lot-history"
              >
                <h2 id="correction-lot-history">{t('approvals.section.lot_history')}</h2>
                <p className="simsm-corrections__section-lede">{t('approvals.lot_history.lede')}</p>
                <MudTable
                  className="simsm-table"
                  columns={historyColumns}
                  rows={historyRows}
                  rowStyle="zebra"
                  aria-label={t('approvals.lot_history.table_aria_label')}
                >
                  <EmptyRegisterMessage>{t('approvals.lot_history.empty')}</EmptyRegisterMessage>
                  {lotHistory.map((entry, index) => (
                    <span
                      key={`changed-by-${entry.correctionRequestId}`}
                      slot={`cell-changedBy-${index}`}
                      className="simsm-corrections__mono"
                    >
                      {entry.changedByIdnp || t('approvals.decided.unknown_actor')}
                    </span>
                  ))}
                  {lotHistory.map((entry, index) => (
                    <span
                      key={`request-${entry.correctionRequestId}`}
                      slot={`cell-request-${index}`}
                    >
                      {entry.correctionRequestId === request.id ? (
                        t('approvals.lot_history.this_request')
                      ) : (
                        <RouterLink
                          to={`/system-admin/approvals/${entry.correctionRequestId}`}
                          size="sm"
                        >
                          {t('approvals.lot_history.open_request')}
                        </RouterLink>
                      )}
                    </span>
                  ))}
                </MudTable>
              </section>
            </div>

            {errorKey ? (
              <MudInfoBox
                emphasis="strong"
                role="alert"
                variant="error"
                titleText={t('approvals.error.title')}
              >
                {t(errorKey)}
              </MudInfoBox>
            ) : null}

            <div className="simsm-corrections__actions">
              <MudButton
                shape="circular"
                variant="neutral"
                size="md"
                onClick={() => navigate(backTo)}
              >
                {isPending ? t('approvals.action.cancel') : t('approvals.action.back_to_list')}
              </MudButton>
              {isPending ? (
                <>
                  <MudButton
                    shape="circular"
                    variant="destructive"
                    size="md"
                    onClick={() => {
                      setAttempted(null);
                      setRejectOpen(true);
                    }}
                  >
                    {t('approvals.action.reject')}
                  </MudButton>
                  <MudButton
                    shape="circular"
                    variant="primary"
                    size="md"
                    disabled={approve.isPending}
                    onClick={tryApprove}
                  >
                    {decidedExpiryValid && decidedExpiry !== request.proposedExpiry
                      ? t('approvals.action.approve_edited')
                      : t('approvals.action.approve_proposed')}
                  </MudButton>
                </>
              ) : null}
            </div>

            <MudModal
              closeLabel={t('common.close')}
              open={rejectOpen}
              // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — applied
              // to every modal uniformly; a no-op here until a descendant emits its own
              // mudClose.
              onMudClose={onModalDismiss(() => setRejectOpen(false))}
            >
              <h2 slot="title">{t('approvals.reject.title')}</h2>
              <MudTextarea
                label={t('approvals.field.rejection_reason')}
                required={true}
                value={rejectionReason}
                maxLength={500}
                placeholder={t('approvals.reject.placeholder')}
                invalid={rejectReasonMissing}
                {...(rejectReasonMissing
                  ? { errorText: t('approvals.reject.reason_required') }
                  : { helperText: t('approvals.reject.reason_help') })}
                onMudInput={(e: CustomEvent<{ value: string }>) => {
                  setRejectionReason(e.detail.value);
                  setAttempted(null);
                }}
              />
              <div slot="actions" className="simsm-corrections__actions">
                <MudButton
                  shape="circular"
                  variant="neutral"
                  size="md"
                  onClick={() => setRejectOpen(false)}
                >
                  {t('approvals.action.cancel')}
                </MudButton>
                <MudButton
                  shape="circular"
                  variant="destructive"
                  size="md"
                  disabled={reject.isPending}
                  onClick={tryReject}
                >
                  {t('approvals.action.confirm_reject')}
                </MudButton>
              </div>
            </MudModal>
          </>
        ) : null}
      </QueryState>
    </section>
  );
}
